import { useEffect, useMemo, useState } from "react";
import { INavigationMenuItem } from "../types/NavigationMenu";
import { dmnIdentity } from "../utils/dmnIdentity";
import {
  getEnvironmentProps,
  GetEnvironmentPropsResponse
} from "../utils/environment";
import { getFilterNavigationItems } from "../utils/navigation/filterNavigationItems";
import { useFeatureFlagContext } from "./useFeatureFlagContext";
import usePermissions from "./usePermissions";
import { useUserContext } from "./useUserContext";

const useNavBarItems = () => {
  const [environmentProps, setEnvironmentProps] =
    useState<GetEnvironmentPropsResponse>();
  const permissions = usePermissions();
  const userContext = useUserContext();
  const { isFlagEnabled } = useFeatureFlagContext();
  const loading = !environmentProps || permissions.loading;

  useEffect(() => {
    (async () => {
      const token = await dmnIdentity.getAccessToken();
      setEnvironmentProps(getEnvironmentProps(token));
    })();
  }, []);

  const filteredItems = useMemo(
    () =>
      getFilterNavigationItems(
        permissions,
        environmentProps,
        isFlagEnabled,
        userContext
      ),
    [permissions, environmentProps, isFlagEnabled]
  );

  const { topItems, bottomItems } = useMemo(() => {
    return filteredItems.reduce(
      (acc, item) => {
        if (item.bottom) {
          acc.bottomItems.push(item);
        } else {
          acc.topItems.push(item);
        }
        return acc;
      },
      {
        topItems: [] as INavigationMenuItem[],
        bottomItems: [] as INavigationMenuItem[]
      }
    );
  }, [filteredItems]);

  const response = useMemo(
    () => ({
      items: filteredItems,
      filteredItems,
      topItems,
      bottomItems,
      loading
    }),
    [filteredItems, topItems, bottomItems, loading]
  );

  return response;
};

export default useNavBarItems;
